import {
  countWords, draftKey, exportBackup, goalKey, importBackup, localDay,
  readVersions, restoreVersion, saveDraft, writingStats,
} from './writer-data.mjs';
import { setupCloud } from './cloud-sync.js';

const editor = document.querySelector('#draft');
const now = new Date();
const day = localDay(now);
const goalInput = document.querySelector('#daily-goal');
const saveStatus = document.querySelector('#save-status');
const historyDialog = document.querySelector('#history-dialog');
const statsDialog = document.querySelector('#stats-dialog');
const focusButton = document.querySelector('#focus-button');
const fmt = (value) => value.toLocaleString('en-US');
let saveTimer;
let cloud = { schedule() {} };

try {
  editor.value = localStorage.getItem(draftKey(day)) ?? '';
  const goal = Number(localStorage.getItem(goalKey));
  if (Number.isInteger(goal) && goal >= 50 && goal <= 50000) goalInput.value = String(goal);
} catch {
  saveStatus.textContent = 'Browser storage is unavailable. Download your writing before leaving.';
}

function render() {
  const words = countWords(editor.value);
  const goal = Number(goalInput.value) || 500;
  const percentage = Math.min(100, Math.round(words / goal * 100));
  document.querySelector('#word-count').textContent = fmt(words);
  document.querySelector('#track-fill').style.height = `${percentage}%`;
  document.querySelector('#climber').style.bottom = `${Math.min(94, Math.max(3, percentage))}%`;
  document.querySelector('#track-goal').textContent = `${fmt(goal)} words`;
  document.querySelector('#progress-copy').textContent = words >= goal
    ? 'Daily goal reached. Keep climbing!'
    : words ? `${fmt(goal - words)} words to your goal.` : 'Your first step is waiting.';
}

function save() {
  window.clearTimeout(saveTimer);
  try {
    saveDraft(localStorage, day, editor.value);
    saveStatus.textContent = 'Saved on this device';
    cloud.schedule(day, editor.value, Number(goalInput.value) || 500);
    return true;
  } catch {
    saveStatus.textContent = 'Could not save. Download your writing now.';
    return false;
  }
}

function download(text, filename, type) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

editor.addEventListener('input', () => {
  render();
  saveStatus.textContent = 'Saving…';
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(save, 400);
});
goalInput.addEventListener('change', () => {
  const goal = Math.min(50000, Math.max(50, Math.round(Number(goalInput.value) || 500)));
  goalInput.value = String(goal);
  try { localStorage.setItem(goalKey, String(goal)); }
  catch { saveStatus.textContent = 'Could not save your goal in this browser.'; }
  cloud.schedule(day, editor.value, goal);
  render();
});
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') save(); });
window.addEventListener('pagehide', save);
document.querySelector('#download').addEventListener('click', () => {
  save();
  download(editor.value, `climbwriter-${day}.txt`, 'text/plain;charset=utf-8');
});
document.querySelector('#backup-download').addEventListener('click', () => {
  if (!save()) return;
  try {
    download(JSON.stringify(exportBackup(localStorage), null, 2),
      `climbwriter-backup-${day}.json`, 'application/json;charset=utf-8');
  } catch {
    saveStatus.textContent = 'Could not create a backup. Try the TXT download.';
  }
});

const backupFile = document.querySelector('#backup-file');
document.querySelector('#backup-import').addEventListener('click', () => backupFile.click());
backupFile.addEventListener('change', async () => {
  const file = backupFile.files?.[0];
  backupFile.value = '';
  if (!file) return;
  if (file.size > 10_000_000) {
    saveStatus.textContent = 'Backup file is too large (10 MB limit).';
    return;
  }
  if (!save()) return;
  try {
    const result = importBackup(localStorage, JSON.parse(await file.text()));
    editor.value = localStorage.getItem(draftKey(day)) ?? editor.value;
    render();
    saveStatus.textContent = `${result.added} day(s) imported. ${result.conflicts} differing day(s) kept unchanged; the backup file still contains them.`;
  } catch (error) {
    saveStatus.textContent = error instanceof Error ? error.message : 'Could not import this backup.';
  }
});

function showHistory() {
  save();
  const list = document.querySelector('#history-list');
  list.replaceChildren();
  let versions = [];
  try { versions = readVersions(localStorage, day); }
  catch { /* The empty-state message remains useful. */ }
  if (!versions.length) {
    const p = document.createElement('p');
    p.textContent = 'No recovery points yet. They are created as you edit an existing draft.';
    list.append(p);
  }
  for (const version of versions) {
    const row = document.createElement('div');
    row.className = 'history-row';
    const info = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = new Date(version.savedAt).toLocaleString('en-US');
    const detail = document.createElement('span');
    detail.textContent = `${fmt(countWords(version.content))} words · ${version.content.slice(0, 80).replace(/\s+/g, ' ') || 'Empty draft'}`;
    info.append(title, detail);
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Restore';
    button.addEventListener('click', () => {
      try {
        editor.value = restoreVersion(localStorage, day, version);
        render();
        saveStatus.textContent = 'Recovery point restored. Your previous draft was kept as a recovery point.';
        historyDialog.close();
      } catch {
        saveStatus.textContent = 'Could not restore this version. Your current draft was left unchanged.';
        historyDialog.close();
      }
    });
    row.append(info, button);
    list.append(row);
  }
  historyDialog.showModal();
}
document.querySelector('#history-button').addEventListener('click', showHistory);

document.querySelector('#stats-button').addEventListener('click', () => {
  save();
  try {
    const stats = writingStats(localStorage, day);
    document.querySelector('#stat-days').textContent = fmt(stats.daysWritten);
    document.querySelector('#stat-total').textContent = fmt(stats.totalWords);
    document.querySelector('#stat-streak').textContent = fmt(stats.streak);
    const bars = document.querySelector('#stats-bars');
    bars.replaceChildren();
    const max = Math.max(1, ...stats.lastSeven.map(item => item.words));
    for (const item of stats.lastSeven) {
      const column = document.createElement('div');
      column.className = 'stats-column';
      const amount = document.createElement('strong');
      amount.textContent = fmt(item.words);
      const bar = document.createElement('span');
      bar.className = 'stats-bar';
      bar.style.height = `${Math.max(4, Math.round(item.words / max * 100))}%`;
      const date = document.createElement('small');
      date.textContent = new Date(`${item.day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
      column.append(amount, bar, date);
      bars.append(column);
    }
    statsDialog.showModal();
  } catch {
    saveStatus.textContent = 'Statistics are unavailable because browser storage could not be read.';
  }
});

function setFocus(active) {
  document.body.classList.toggle('is-focus', active);
  focusButton.setAttribute('aria-pressed', String(active));
  focusButton.firstChild.textContent = active ? 'Exit focus mode ' : 'Focus mode ';
  if (active) editor.focus();
}
focusButton.addEventListener('click', () => setFocus(!document.body.classList.contains('is-focus')));
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && document.body.classList.contains('is-focus')) setFocus(false);
});
document.querySelectorAll('[data-close-dialog]').forEach(button => {
  button.addEventListener('click', () => button.closest('dialog').close());
});
document.querySelector('#date-label').textContent = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
document.querySelector('#year').textContent = String(now.getFullYear());
render();
cloud = setupCloud({ day, editor, goalInput, saveStatus, render });
